import {Component, OnDestroy, OnInit} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {AuthService} from '../../_services/auth.service';
import {TokenStorageService} from '../../_services/token-storage.service';
import {NewMonitorAccount, StaffAccount, UserAccount, UserAccountType} from '../../models/eventRequest';
import {ROLE_LABELS} from '../../profile/user-roles/user-roles.component';

declare let $: any;

const TYPE_LABELS: Record<UserAccountType, string> = {STUDENT: 'Élève', MONITOR: 'Moniteur', STAFF: 'Personnel'};

/** Libellés des champs du formulaire, pour indiquer ceux à corriger. */
const FIELD_LABELS: Record<string, string> = {
  lastname: 'nom (3 lettres min.)', firstname: 'prénom (3 lettres min.)', phoneNumber: 'téléphone (0XXXXXXXXX)',
  username: "nom d'utilisateur", email: 'email valide', password: 'mot de passe (6 caractères min.)'
};

/**
 * Gestion des comptes : rôles de tous les comptes, création des moniteurs (fiche + compte), création et
 * modification des comptes du personnel (secrétariat...). Les moniteurs et élèves se modifient depuis leur fiche.
 */
@Component({
  selector: 'app-user-accounts',
  templateUrl: './user-accounts.component.html',
  styleUrls: ['./user-accounts.component.css']
})
export class UserAccountsComponent implements OnInit, OnDestroy {

  accounts: UserAccount[] = [];
  search = '';
  includeStudents = false;
  includeDisabled = false;
  loading = false;
  messageError = '';

  /** Compte dont on gère les rôles (fenêtre « Rôles »). */
  rolesAccount: UserAccount | null = null;

  /** Formulaire création (editedId null) / modification d'un compte du personnel. */
  editedId: number | null = null;
  /** Type du compte modifié : un moniteur / élève voit l'email de sa fiche suivre celui du compte. */
  editedType: UserAccountType | null = null;
  /** Type de compte créé : moniteur (fiche + compte) ou personnel. */
  newType: 'MONITOR' | 'STAFF' = 'MONITOR';
  form: StaffAccount = this.emptyForm();
  monitorForm = this.emptyMonitorForm();
  formError = '';
  saving = false;

  private search$ = new Subject<void>();
  private subscription?: Subscription;

  constructor(private authService: AuthService, private tokenStorage: TokenStorageService) { }

  ngOnInit(): void {
    this.subscription = this.search$.pipe(debounceTime(300)).subscribe(() => this.load());
    this.load();
    // les rôles ont pu changer dans la fenêtre : on rafraîchit la liste à sa fermeture
    $(document).on('hidden.bs.modal', '#accountRolesModal', () => {
      this.rolesAccount = null;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    $(document).off('hidden.bs.modal', '#accountRolesModal');
  }

  onSearchChange() {
    this.search$.next();
  }

  load() {
    this.loading = true;
    this.messageError = '';
    this.authService.getAccounts(this.search, this.includeStudents).subscribe(
      data => {
        this.accounts = data;
        this.loading = false;
      },
      err => {
        this.messageError = typeof err.error === 'string' ? err.error : 'Impossible de charger les comptes';
        this.loading = false;
      }
    );
  }

  /** Comptes affichés : désactivés masqués par défaut, comptes actifs en premier (puis ordre alphabétique du back). */
  get displayedAccounts(): UserAccount[] {
    return this.accounts
      .filter(a => this.includeDisabled || a.enabled)
      .sort((a, b) => Number(b.enabled) - Number(a.enabled));
  }

  get disabledCount(): number {
    return this.accounts.filter(a => !a.enabled).length;
  }

  typeLabel(account: UserAccount): string {
    return TYPE_LABELS[account.type];
  }

  roleLabel(role: string): string {
    return ROLE_LABELS[role] || (role === 'ROLE_USER' ? 'Élève' : role);
  }

  get isSuperAdmin(): boolean {
    return (this.tokenStorage.getUser()?.roles ?? []).includes('ROLE_SUPER_ADMIN');
  }

  /** Rôles proposés à la création (pour un moniteur : en plus du rôle Moniteur). */
  get staffRoles(): string[] {
    return ['ROLE_ADMIN', 'ROLE_READER', ...(this.isSuperAdmin ? ['ROLE_SUPER_ADMIN'] : [])];
  }

  /** Compte d'accès modifiable par tous, sauf celui d'un super admin pour un admin (règle du back). */
  canEditAccount(account: UserAccount): boolean {
    return this.isSuperAdmin || !account.roles.includes('ROLE_SUPER_ADMIN');
  }

  editTitle(account: UserAccount): string {
    return this.canEditAccount(account) ? 'Modifier le nom d\'utilisateur, l\'email ou le mot de passe'
      : 'Seul un super admin peut modifier le compte d\'un super admin';
  }

  /** Pas sur son propre compte ; un admin ne touche pas au compte d'un super admin (règles du back). */
  canToggle(account: UserAccount): boolean {
    return account.id !== this.tokenStorage.getUser()?.id
      && (this.isSuperAdmin || !account.roles.includes('ROLE_SUPER_ADMIN'));
  }

  /** Active / désactive le compte. */
  toggleAccount(account: UserAccount) {
    this.authService.setAccountEnabled(account.id, !account.enabled).subscribe(
      updated => account.enabled = updated.enabled,
      err => this.messageError = typeof err.error === 'string' ? err.error : 'La modification du compte a échoué'
    );
  }

  openRoles(account: UserAccount) {
    this.rolesAccount = account;
    $('#accountRolesModal').modal('show');
  }

  openCreate() {
    this.editedId = null;
    this.newType = 'MONITOR';
    this.form = this.emptyForm();
    this.monitorForm = this.emptyMonitorForm();
    this.formError = '';
    $('#staffAccountModal').modal('show');
  }

  openEdit(account: UserAccount) {
    this.editedId = account.id;
    this.editedType = account.type;
    this.form = {username: account.username, email: account.email, password: null};
    this.formError = '';
    $('#staffAccountModal').modal('show');
  }

  toggleRole(role: string) {
    const roles = this.form.roles ?? [];
    this.form.roles = roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role];
  }

  save(f: NgForm) {
    if (f.invalid) {
      Object.values(f.controls).forEach(control => control.markAsTouched());
      const invalid = Object.keys(f.controls).filter(name => f.controls[name].invalid).map(name => FIELD_LABELS[name] || name);
      this.formError = 'Merci de compléter : ' + invalid.join(', ');
      return;
    }
    const creatingStaff = this.editedId === null && this.newType === 'STAFF';
    if (creatingStaff && !(this.form.roles ?? []).length) {
      this.formError = 'Choisissez au moins un rôle';
      return;
    }
    const account: StaffAccount = {...this.form, password: this.form.password || null};
    const request = this.editedId !== null
      ? this.authService.updateStaffAccount(this.editedId, account)
      : creatingStaff
        ? this.authService.createStaffAccount(account)
        : this.authService.createMonitorAccount(this.toMonitorAccount());
    this.saving = true;
    this.formError = '';
    request.subscribe(
      () => {
        this.saving = false;
        $('#staffAccountModal').modal('hide');
        this.load();
      },
      err => {
        this.saving = false;
        this.formError = typeof err.error === 'string' ? err.error
          : 'Vérifiez les champs (email valide, mot de passe de 6 caractères minimum, nom et prénom de 3 caractères minimum)';
      }
    );
  }

  private toMonitorAccount(): NewMonitorAccount {
    return {
      monitor: {...this.monitorForm, email: this.form.email},
      username: this.form.username,
      password: this.form.password ?? '',
      roles: this.form.roles ?? []
    };
  }

  private emptyMonitorForm() {
    return {sexe: 'M', firstname: '', lastname: '', phoneNumber: '', color: '#40f81b'};
  }

  private emptyForm(): StaffAccount {
    return {username: '', email: '', password: '', roles: []};
  }
}
